/**
 * @file Body of the "Derived values" drawer on an open form (SYD-2404 §11, per-form
 * view, widened by SYD-2451): one row per configured derive with the stamp THIS form
 * version carries — state, last calculated, and the configuration revision it ran
 * under — under a summary of whether the form's values are current, plus
 * "Recalculate this form". Anyone who may ask for a recompute may open it; the
 * callable grants on the write right on the form, not on `formsAdmin`.
 * Consumer: `FormDerivedValuesDrawer`, which mounts it behind `destroyOnHidden` so
 * a closed drawer holds no subscription to `formStore`.
 * Opening the drawer READS and never refreshes: a pass on a read path would put a
 * write on every viewer, and `firestore.rules` denies every client write to
 * `formDerives` on both lanes anyway. The summary compares the version's stored
 * `rev` against the FormType's `formDeriveRev` and states when the values were last
 * calculated, which is what makes a gates-only configuration safe — a stale total is
 * normal operation, and the drawer says so rather than warning about it.
 * Recalculating goes through the `formDeriveRecomputeForm` callable, never a write.
 * A skipped stamp records when the skip was noticed, never a value, so its
 * last-calculated stays "Never" — the same rule `formDeriveStatus` applies.
 * The State column and the table/column labels come from the shared
 * `formDeriveStateLabel` and `useFormDeriveLabelResolvers`, so this drawer and the
 * pane's Calculation health panel cannot word one derive two ways.
 */
import { FC, useMemo, useState } from 'react';
import { Alert, Button, Typography } from 'antd';
import ResponsiveTable from '@/components/atoms/ResponsiveTable';
import { useSnapshot } from 'valtio';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import FlexBox from '../../atoms/FlexBox';
import FlexCol from '../../atoms/FlexCol';
import { formStore } from '../../../store';
import { createFormDeriveEngineConfig } from '#types';
import type { FormDeriveId } from '#types';
import {
  formDeriveCopy,
  formDeriveEntryName,
  formDeriveNamesATable,
  formDeriveStateLabel,
} from '@/services/formDerives';
import { useFormDeriveLabelResolvers } from '@/use/useFormDeriveLabelResolvers';
import { callFormDeriveRecomputeForm } from '@/services/callFormDeriveRecomputeForm';

dayjs.extend(relativeTime);

const { Text } = Typography;

/** One rendered row, already worded — the table decides nothing. */
type DerivedValueRow = {
  deriveId: FormDeriveId;
  name: string;
  state: string;
  lastCalculated: string;
  revision: string;
};

/**
 * Millis from a Firestore Timestamp WITHOUT calling a method on it: the value
 * arrives through a Valtio snapshot, where only `seconds`/`nanoseconds` survive.
 */
const timestampMillis = (stamp: { seconds: number; nanoseconds: number }): number =>
  stamp.seconds * 1000 + stamp.nanoseconds / 1_000_000;

/**
 * Per-form derive stamps and the per-form recalculation request.
 * @component
 */
export const FormDerivedValuesPanel: FC = () => {
  const snap = useSnapshot(formStore.state);
  const [requesting, setRequesting] = useState(false);
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const formDerives = snap.formType.data?.formDerives;
  const formDeriveRev = snap.formType.data?.formDeriveRev;
  const config = useMemo(
    () => createFormDeriveEngineConfig({ formDerives, formDeriveRev }),
    [formDerives, formDeriveRev],
  );

  const versionState = snap.formVersion.data?.formDerives;
  const formId = snap.formId;
  const versionId = snap.versionId;

  /**
   * The shared resolvers, with the reference slot named by the RECORD sitting in it
   * rather than by the slot: a filler knows "Acme Corp", not "Customer reference".
   */
  const labels = useFormDeriveLabelResolvers(
    (referenceKey) => snap.formVersion.data?.entityReferences?.[referenceKey]?.entityRecordName,
    config.entries.some(formDeriveNamesATable),
  );

  const rows: DerivedValueRow[] = config.entries.map((entry) => {
    const stamp = versionState?.stamps?.[entry.deriveId];
    const calculated = stamp !== undefined && stamp.skipped === undefined;
    return {
      deriveId: entry.deriveId,
      name: formDeriveEntryName(entry, labels),
      state: formDeriveStateLabel(stamp, config.rev),
      lastCalculated:
        calculated && stamp !== undefined
          ? dayjs(timestampMillis(stamp.computedAt)).fromNow()
          : formDeriveCopy.neverCalculated,
      revision: stamp === undefined ? '' : String(stamp.rev),
    };
  });

  /**
   * The whole form's reading, off the version's own `rev` rather than off the
   * stamps: `rev` is the configuration revision the last full pass ran under, so a
   * config edit makes every value stale at once and one sentence can say it.
   */
  const summaryState = formDeriveStateLabel({ rev: versionState?.rev }, config.rev);

  /**
   * The newest stamp any derive on this version carries. A skipped stamp records
   * when the skip was noticed and never a value, so it cannot date a calculation.
   */
  const lastCalculatedMillis = config.entries.reduce<number | null>((newest, entry) => {
    const stamp = versionState?.stamps?.[entry.deriveId];
    if (stamp === undefined || stamp.skipped !== undefined) return newest;
    const millis = timestampMillis(stamp.computedAt);
    return newest === null || millis > newest ? millis : newest;
  }, null);

  const lastCalculated = `${formDeriveCopy.lastCalculatedColumnHeader}: ${
    lastCalculatedMillis === null
      ? formDeriveCopy.neverCalculated
      : dayjs(lastCalculatedMillis).fromNow()
  }`;

  /**
   * The ids come from the render-time snapshot, never off the proxy after the
   * await: a post-await read could name a different form than the one clicked.
   * `aborted` is the server's transient refusal — a computation holds this
   * version — and is the only rejection worth its own words.
   */
  const requestRecalculation = async (): Promise<void> => {
    if (formId === undefined || versionId === undefined) {
      setNotice({ type: 'error', text: formDeriveCopy.recalculateThisFormFailed });
      return;
    }
    setRequesting(true);
    setNotice(null);
    try {
      await callFormDeriveRecomputeForm(formId, versionId);
      setNotice({ type: 'success', text: formDeriveCopy.recalculateThisFormQueued });
    } catch (error) {
      setNotice({
        type: 'error',
        text:
          (error as { code?: unknown }).code === 'functions/aborted'
            ? formDeriveCopy.recalculateThisFormBusy
            : formDeriveCopy.recalculateThisFormFailed,
      });
    } finally {
      setRequesting(false);
    }
  };

  return (
    <FlexCol stretch>
      <Text type="secondary">{formDeriveCopy.perFormSubLine}</Text>

      {notice !== null && <Alert type={notice.type} title={notice.text} />}

      {config.entries.length === 0 ? (
        <Text type="secondary">{formDeriveCopy.perFormEmpty}</Text>
      ) : (
        <>
          <FlexBox noGrow justifyStart>
            <Text type="secondary">{`${summaryState} · ${lastCalculated}`}</Text>
          </FlexBox>

          <ResponsiveTable<DerivedValueRow>
            dataSource={rows}
            rowKey="deriveId"
            pagination={false}
            size="small"
            tableLayout="fixed"
            columns={[
              { title: formDeriveCopy.deriveColumnHeader, dataIndex: 'name', key: 'name' },
              { title: formDeriveCopy.stateColumnHeader, dataIndex: 'state', key: 'state' },
              {
                title: formDeriveCopy.lastCalculatedColumnHeader,
                dataIndex: 'lastCalculated',
                key: 'lastCalculated',
              },
              {
                title: formDeriveCopy.revisionColumnHeader,
                dataIndex: 'revision',
                key: 'revision',
              },
            ]}
          />
        </>
      )}

      <FlexBox noGrow justifyStart>
        <Button
          onClick={() => void requestRecalculation()}
          loading={requesting}
          disabled={
            requesting ||
            config.entries.length === 0 ||
            formId === undefined ||
            versionId === undefined
          }
          aria-label="Recalculate the derived values on this form"
        >
          {formDeriveCopy.recalculateThisForm}
        </Button>
      </FlexBox>
    </FlexCol>
  );
};
